/**
 * Family registration fee display helpers (amount from site settings).
 */

import {
  formatFamilyRegistrationFeeUsd,
} from '@lambda/shared/familyRegistrationFee';

export {
  DEFAULT_FAMILY_REGISTRATION_FEE_CENTS,
  formatFamilyRegistrationFeeUsd,
  normalizeFamilyRegistrationFeeCents,
} from '@lambda/shared/familyRegistrationFee';
export {
  familyRegistrationAmountDueCents,
  familyRegistrationDiscountCents,
  normalizeFamilyRegistrationDiscountCents,
} from '@lambda/shared/familyRegistrationDiscount';

/** Intro sentence on the family registration form header. */
export function familyRegistrationFeeIntroLine(feeCents: number): string {
  const amount = formatFamilyRegistrationFeeUsd(feeCents);
  return `We require a yearly ${amount} Family Registration fee (once for the whole year—Fall and Spring semesters) to be paid on the first day of class. `;
}

/** How-to-join step 6 description on the landing page. */
export function familyRegistrationFeeHowToJoinDescription(feeCents: number): string {
  const amount = formatFamilyRegistrationFeeUsd(feeCents);
  return `On the first day of the semester, pay your ${amount} Family Registration Fee in cash at the front desk.`;
}

function familyRegistrationWhoLabel(parentName: string, familyName?: string): string {
  return familyName?.trim() && familyName.trim() !== '—'
    ? `the ${familyName.trim()} family`
    : parentName.trim() || 'this family';
}

/** Admin mark-paid confirmation body (feeFormatted already like "$30"). */
export function familyRegistrationFeeMarkPaidDescription(
  feeFormatted: string,
  parentName: string,
  familyName?: string,
  remainingDueFormatted?: string
): string {
  const who = familyRegistrationWhoLabel(parentName, familyName);
  if (remainingDueFormatted && remainingDueFormatted !== feeFormatted) {
    if (remainingDueFormatted === '$0') {
      return `A full discount is on this registration, so no ${feeFormatted} is due. Record it as paid (cash) for ${who}? They will get a notice that their fee was marked paid.`;
    }
    return `Record the remaining ${remainingDueFormatted} family registration fee as paid (cash) for ${who}? The yearly fee is ${feeFormatted} after the discount. They will get a notice that their fee was marked paid.`;
  }
  return `Record the ${feeFormatted} family registration fee as paid (cash) for ${who}? They will get a notice that their fee was marked paid.`;
}

/** Intro for the apply/edit yearly-fee discount modal. */
export function familyRegistrationFeeApplyDiscountDescription(
  feeFormatted: string,
  parentName: string,
  familyName?: string
): string {
  const who = familyRegistrationWhoLabel(parentName, familyName);
  return `Enter a dollar amount to take off the ${feeFormatted} yearly fee for ${who}. The remaining amount stays unpaid until you Mark Paid. Collected totals use what is still due.`;
}

/** Confirm removing a yearly-fee discount. */
export function familyRegistrationFeeRemoveDiscountDescription(
  parentName: string,
  familyName?: string
): string {
  const who = familyRegistrationWhoLabel(parentName, familyName);
  return `Remove the yearly-fee discount for ${who}? The full fee will be due again until it is marked paid.`;
}

/** Dollars input → cents for API (whole dollars). */
export function familyRegistrationFeeDollarsToCents(dollars: number): number {
  if (!Number.isFinite(dollars)) return 0;
  return Math.round(dollars * 100);
}

/** Cents → whole dollars for admin form field. */
export function familyRegistrationFeeCentsToDollars(cents: number): number {
  return cents / 100;
}
